import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  Suspense
} from 'react';
import './App.css';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import ApplicationService from './services/ApplicationService';
import { Provider } from 'react-redux';
import store from './redux/store';
import { initConfig } from './redux/actions';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import InterceptorAxios from './utils/InterceptorAxios';
import 'react-datasheet/lib/react-datasheet.css';
import { ThemeProvider, Theme } from '@material-ui/core/styles';
import 'typeface-roboto';
import './utils/i18n';
import { createTheme } from './utils/Theme';
import TelemetryProvider from './utils/TelemetryProvider';
import { IAppConfig } from './data-models/dataModels';
import withClearCache from './ClearCache';
import PrivateRoute from './components/RouterComponent';

import {
  ProjectComponentPage,
  HomeComponentPage,
  ConstructionPortalPage,
  UnAuthorizedComponentPage,
  SnapshotImageComponentPage,
  ProjectComponentPageForExternalUser
} from './components/Routes/index';
import PBINewWindowWrapper from './components/report-view/NewWindow/PBINewWindowWrapper';
import PhasingNewWindowWrapper from './components/phasing-new-window/PhasingNewWindowWrapper';
import { LoadProjectSkeleton } from './utils/loadingComponents/LoadSkeleton';
const BiReportNewWindowLoader = React.lazy(() => import('./components/report-view/NewWindow/PBIReportNewWindowLoader'));
const PhasingNewWindowLoader = React.lazy(() => import('./components/phasing-new-window/PhasingNewWindowLoader'));


InterceptorAxios.configure();

const theme: Theme = createTheme();

const MainApp: FunctionComponent = () => {
  const [isReady, setIsReady] = useState(false);
  const [config, setConfig] = useState<IAppConfig>();

  const getConfig = useCallback(async () => {
    const config = await ApplicationService.getAppConfig();
    store.dispatch(initConfig(config));
    setConfig(config);
    setIsReady(true);
  }, [setConfig]);

  useEffect(() => {
    sessionStorage.removeItem("panel-reposition-ids");
  },[]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        await getConfig();
      } catch (e) {
        console.error('config not load', (e as {message?: string})?.message);
      }
    };
  
    fetchConfig();
  }, [getConfig]);

  // Set up a CSP meta tag when the component mounts
  useEffect(() => {
    // Define your CSP content
    if (config) {
      //Test comment
      const cspContent = `
        default-src ${config.cspDefaultSrc};
        script-src ${config.cspScriptSrc};
        style-src ${config.cspStyleSrc};
        font-src ${config.cspFontSrc};
        media-src ${config.cspMediaSrc};
        img-src ${config.cspImgSrc};
      `;

      // Create a new CSP meta tag
      const cspMeta = document.createElement('meta');
      cspMeta.httpEquiv = 'Content-Security-Policy';
      cspMeta.content = cspContent.trim();

      // Append the meta tag to the document's head
      document.head.appendChild(cspMeta);

      // Cleanup function to remove the meta tag when the component unmounts
      return () => {
        cspMeta.remove();
      };
    }
  }, [config]);

  useEffect(() => {
    if (isReady && config?.isLoginEntryOnPageLoadAllowed) {
      setTimeout(() => {
        ApplicationService.insertUserLogin()
          .catch(() => {
            console.log('Login Entry Error');
          });
      }, 3000);
    }
  }, [isReady]);

  return (
    <Suspense fallback={<LoadProjectSkeleton/>}>
    <Provider store={store}>
      <BrowserRouter>
        {isReady ? (
          <PBINewWindowWrapper>
          <PhasingNewWindowWrapper>
            <TelemetryProvider instrumentationKey={config!.InstrumentationKey}>
              <ThemeProvider theme={theme}>
                <Switch>
                  {/* <Route path='/login' component={LoginComponent} /> */}
                  {/* <PrivateRoute component={HomeComponent} path='/viewer' /> */}
                  <PrivateRoute
                    component={HomeComponentPage}
                    path='/:projectName/viewer'
                  />
                  <PrivateRoute component={BiReportNewWindowLoader} path='/:projectName/power-bi/:id/:selectedPlantId' />
                  <PrivateRoute component={PhasingNewWindowLoader} path='/:projectName/phasing/:selectedPlantId' />
                  <PrivateRoute component={ProjectComponentPage} path='/home' />
                  <Route path='/unAuthorized' component={UnAuthorizedComponentPage} />
                  <PrivateRoute component={ConstructionPortalPage} path='/:projectName/portal' />
                  <PrivateRoute component={ProjectComponentPageForExternalUser} path='/:projectName/widget/:email' />
                  <PrivateRoute path='/:projectName/snapshot/:snapshot' component={SnapshotImageComponentPage} />
                  {/* <PrivateRoute path='/'><Redirect to='/viewer'/></PrivateRoute> */}
                  <PrivateRoute path='/'>
                    <Redirect to='/home' />
                  </PrivateRoute>
                </Switch>
              </ThemeProvider>
            </TelemetryProvider>
          </PhasingNewWindowWrapper>
          </PBINewWindowWrapper>
        ): <LoadProjectSkeleton /> }
      </BrowserRouter>
      <ToastContainer />
    </Provider>
    </Suspense>
  );
};

const ClearCacheComponent = withClearCache(MainApp);

const App: FunctionComponent = () => {
  return <ClearCacheComponent />;
};

export default App;
